// src/components/upmode-agent/publish-confirm-card.tsx
import React, { useState, useMemo } from "react";
import {
  Rocket, X, Edit3, AlertTriangle, FileCode2, Globe, Linkedin, CheckCircle2,
} from "lucide-react";
import { PublishDestination } from "@/app/services/upmode-agent/upmode-agent-api";
import { Alert, AlertTitle, AlertDescription } from "../../components/ui/alert";

export interface PendingPublish {
  tool_use_id: string;
  destinations: PublishDestination[];
  content: Record<string, Record<string, any>>;
  summary: string;
}

interface PublishConfirmCardProps {
  pending: PendingPublish;
  onConfirm: (selected: Array<{ destination: PublishDestination; content: Record<string, any> }>) => Promise<void>;
  onCancel: () => void;
  busy?: boolean;
}

const DEST_META: Record<PublishDestination, { label: string; color: string; Icon: React.ComponentType<{ style?: React.CSSProperties }> }> = {
  hashnode:  { label: "Hashnode",  color: "#2962FF", Icon: FileCode2 },
  wordpress: { label: "WordPress", color: "#21759B", Icon: Globe },
  linkedin:  { label: "LinkedIn",  color: "#0A66C2", Icon: Linkedin },
};

export function PublishConfirmCard({ pending, onConfirm, onCancel, busy }: PublishConfirmCardProps) {
  // Per-destination edited content (starts from the agent's draft)
  const [edited, setEdited] = useState<Record<string, Record<string, any>>>(() => {
    const copy: Record<string, Record<string, any>> = {};
    for (const d of pending.destinations) {
      copy[d] = { ...(pending.content[d] || {}) };
    }
    return copy;
  });

  // Per-destination "included in this publish" flag
  const [included, setIncluded] = useState<Record<string, boolean>>(() => {
    const copy: Record<string, boolean> = {};
    for (const d of pending.destinations) copy[d] = true;
    return copy;
  });

  const [activeTab, setActiveTab] = useState<PublishDestination>(pending.destinations[0]);
  const [editing, setEditing] = useState(false);

  const selectedCount = useMemo(
    () => pending.destinations.filter(d => included[d]).length,
    [pending.destinations, included],
  );

  const updateField = (dest: string, field: string, value: any) => {
    setEdited(prev => ({ ...prev, [dest]: { ...prev[dest], [field]: value } }));
  };

  const handlePublish = async () => {
    const selected = pending.destinations
      .filter(d => included[d])
      .map(d => ({ destination: d, content: edited[d] || {} }));
    if (!selected.length) return;
    await onConfirm(selected);
  };

  return (
    <div
      style={{
        alignSelf: "flex-start",
        maxWidth: "95%",
        width: "100%",
        borderRadius: 12,
        border: "2px solid rgba(16,185,129,0.25)",
        backgroundColor: "rgba(16,185,129,0.04)",
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        minWidth: 0,
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Alert>
            <Rocket />
            <AlertTitle>Ready to publish</AlertTitle>
            <AlertDescription>
              {pending.summary ||
                `The agent drafted content for ${pending.destinations.length} destination${pending.destinations.length === 1 ? "" : "s"}. Review, pick which to publish, then approve.`}
              {pending.destinations.some(d => d !== "wordpress") && (
                <span style={{ display: "block", marginTop: 4 }}>
                  Hashnode and LinkedIn go live, so they publish only a draft version written in the content studio,
                  tested in the wind tunnel and approved. Text drafted here will be refused there; a WordPress draft
                  goes through as before.
                </span>
              )}
            </AlertDescription>
          </Alert>
        </div>
        <button
          onClick={() => setEditing(!editing)}
          disabled={busy}
          style={{
            padding: 7,
            borderRadius: 7,
            border: "1px solid var(--border)",
            backgroundColor: editing ? "rgba(37,99,235,0.08)" : "var(--background)",
            cursor: busy ? "not-allowed" : "pointer",
            color: editing ? "#2563EB" : "var(--muted-foreground)",
            flexShrink: 0,
            height: "fit-content",
          }}
          title={editing ? "Stop editing" : "Edit before publishing"}
          aria-label={editing ? "Stop editing" : "Edit before publishing"}
        >
          <Edit3 style={{ width: 12, height: 12 }} />
        </button>
      </div>

      {/* Destination selection + tabs */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted-foreground)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Destinations ({selectedCount} of {pending.destinations.length} selected)
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {pending.destinations.map(dest => {
            const meta = DEST_META[dest];
            const Icon = meta.Icon;
            const isActive = activeTab === dest;
            const isIncluded = !!included[dest];
            return (
              <div
                key={dest}
                style={{
                  display: "flex", alignItems: "stretch",
                  borderRadius: 8, overflow: "hidden",
                  border: `1px solid ${isActive ? meta.color : "var(--border)"}`,
                  backgroundColor: isActive ? "var(--background)" : "var(--muted)",
                  opacity: isIncluded ? 1 : 0.45,
                  transition: "opacity 0.15s",
                }}
              >
                {/* Include checkbox */}
                <button
                  onClick={() => setIncluded(prev => ({ ...prev, [dest]: !prev[dest] }))}
                  disabled={busy}
                  aria-label={isIncluded ? `Exclude ${meta.label}` : `Include ${meta.label}`}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: "0 8px", border: "none", cursor: busy ? "not-allowed" : "pointer",
                    backgroundColor: isIncluded ? "rgba(16,185,129,0.08)" : "transparent",
                    borderRight: "1px solid var(--border)",
                  }}
                >
                  <div
                    style={{
                      width: 14, height: 14, borderRadius: 3,
                      border: `2px solid ${isIncluded ? "#10B981" : "var(--border)"}`,
                      backgroundColor: isIncluded ? "#10B981" : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    {isIncluded && (
                      <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
                        <path d="M2 5L4 7L8 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                </button>
                {/* Tab body */}
                <button
                  onClick={() => setActiveTab(dest)}
                  disabled={busy}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "7px 11px", border: "none",
                    backgroundColor: "transparent",
                    cursor: busy ? "not-allowed" : "pointer",
                    color: "var(--foreground)",
                    fontSize: 12, fontWeight: 600,
                  }}
                >
                  <Icon style={{ width: 12, height: 12, color: meta.color }} />
                  <span>{meta.label}</span>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Active tab content */}
      <div
        style={{
          backgroundColor: "var(--background)",
          borderRadius: 8,
          padding: 10,
          border: "1px solid var(--border)",
          minWidth: 0,
          opacity: included[activeTab] ? 1 : 0.55,
        }}
      >
        <DestinationEditor
          destination={activeTab}
          content={edited[activeTab] || {}}
          onChange={(field, value) => updateField(activeTab, field, value)}
          editing={editing && !busy}
        />
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <button
          onClick={onCancel}
          disabled={busy}
          style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
            padding: "7px 13px", borderRadius: 7,
            border: "1px solid var(--border)",
            backgroundColor: "var(--muted)",
            cursor: busy ? "not-allowed" : "pointer",
            fontSize: 12, fontWeight: 600, color: "var(--foreground)",
          }}
        >
          <X style={{ width: 11, height: 11 }} />
          Cancel
        </button>
        <button
          onClick={handlePublish}
          disabled={busy || selectedCount === 0}
          style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
            padding: "7px 14px", borderRadius: 7,
            border: "none",
            backgroundColor: busy || selectedCount === 0 ? "var(--border)" : "#10B981",
            color: "white",
            cursor: busy || selectedCount === 0 ? "not-allowed" : "pointer",
            fontSize: 12, fontWeight: 600,
          }}
        >
          <Rocket style={{ width: 11, height: 11 }} />
          {busy
            ? "Publishing…"
            : selectedCount === 0
              ? "Select at least one"
              : selectedCount === pending.destinations.length
                ? `Publish to all (${selectedCount})`
                : `Publish to ${selectedCount}`}
        </button>
      </div>
    </div>
  );
}

// ─── Per-destination editor ────────────────────────────────────────────────────

function DestinationEditor({
  destination, content, onChange, editing,
}: {
  destination: PublishDestination;
  content: Record<string, any>;
  onChange: (field: string, value: any) => void;
  editing: boolean;
}) {
  if (destination === "hashnode") {
    return <HashnodePreview content={content} onChange={onChange} editing={editing} />;
  }
  if (destination === "wordpress") {
    return <WordPressPreview content={content} onChange={onChange} editing={editing} />;
  }
  if (destination === "linkedin") {
    return <LinkedInPreview content={content} onChange={onChange} editing={editing} />;
  }
  return null;
}

function HashnodePreview({
  content, onChange, editing,
}: {
  content: Record<string, any>;
  onChange: (field: string, value: any) => void;
  editing: boolean;
}) {
  const title = content.title || "";
  const markdown = content.content_markdown || "";
  const tags = Array.isArray(content.tags) ? content.tags : [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
      <FieldLabel>Title</FieldLabel>
      {editing ? (
        <input
          value={title}
          onChange={e => onChange("title", e.target.value)}
          maxLength={250}
          style={inputSt}
        />
      ) : (
        <div style={readonlyTitle}>{title || <em style={em}>(no title)</em>}</div>
      )}

      <FieldLabel>Content (Markdown)</FieldLabel>
      {editing ? (
        <textarea
          value={markdown}
          onChange={e => onChange("content_markdown", e.target.value)}
          rows={12}
          style={{ ...inputSt, fontFamily: "ui-monospace, SFMono-Regular, monospace", lineHeight: 1.55, resize: "vertical" }}
        />
      ) : (
        <div style={readonlyBody}>{markdown || <em style={em}>(empty)</em>}</div>
      )}

      {tags.length > 0 && (
        <>
          <FieldLabel>Tags</FieldLabel>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {tags.slice(0, 5).map((t: string, i: number) => (
              <span key={i} style={tagChip}>{t}</span>
            ))}
          </div>
        </>
      )}

      <CharCount current={markdown.length} label="chars" />
    </div>
  );
}

function WordPressPreview({
  content, onChange, editing,
}: {
  content: Record<string, any>;
  onChange: (field: string, value: any) => void;
  editing: boolean;
}) {
  const title = content.title || "";
  const html = content.content_html || "";
  const excerpt = content.excerpt || "";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
      <FieldLabel>Title</FieldLabel>
      {editing ? (
        <input
          value={title}
          onChange={e => onChange("title", e.target.value)}
          maxLength={200}
          style={inputSt}
        />
      ) : (
        <div style={readonlyTitle}>{title || <em style={em}>(no title)</em>}</div>
      )}

      <FieldLabel>Content (HTML)</FieldLabel>
      {editing ? (
        <textarea
          value={html}
          onChange={e => onChange("content_html", e.target.value)}
          rows={12}
          style={{ ...inputSt, fontFamily: "ui-monospace, SFMono-Regular, monospace", lineHeight: 1.55, resize: "vertical" }}
        />
      ) : (
        <div style={readonlyBody}>{html || <em style={em}>(empty)</em>}</div>
      )}

      {excerpt && (
        <>
          <FieldLabel>Excerpt</FieldLabel>
          {editing ? (
            <input
              value={excerpt}
              onChange={e => onChange("excerpt", e.target.value)}
              maxLength={300}
              style={inputSt}
            />
          ) : (
            <div style={{ ...readonlyBody, fontSize: 11, color: "var(--muted-foreground)" }}>{excerpt}</div>
          )}
        </>
      )}

      <CharCount current={html.length} label="chars" />
    </div>
  );
}

function LinkedInPreview({
  content, onChange, editing,
}: {
  content: Record<string, any>;
  onChange: (field: string, value: any) => void;
  editing: boolean;
}) {
  const text = content.text || "";
  const over = text.length > 3000;
  const soft = text.length > 1300;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
      <FieldLabel>Post text</FieldLabel>
      {editing ? (
        <textarea
          value={text}
          onChange={e => onChange("text", e.target.value)}
          rows={10}
          style={{ ...inputSt, lineHeight: 1.55, resize: "vertical" }}
        />
      ) : (
        <div style={{ ...readonlyBody, whiteSpace: "pre-wrap" }}>{text || <em style={em}>(empty)</em>}</div>
      )}

      <CharCount
        current={text.length}
        label="chars"
        warning={over ? "Over 3000 char hard limit — will be rejected" : soft ? "Over 1300 chars — LinkedIn will truncate in feed preview" : undefined}
      />
    </div>
  );
}

// ─── Small components + styles ─────────────────────────────────────────────────

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 10, fontWeight: 700, color: "var(--muted-foreground)",
        textTransform: "uppercase", letterSpacing: "0.05em",
      }}
    >
      {children}
    </div>
  );
}

function CharCount({ current, label, warning }: { current: number; label: string; warning?: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      <span style={{ fontSize: 10, color: "var(--muted-foreground)" }}>
        {current} {label}
      </span>
      {warning && (
        <span style={{ fontSize: 10, color: "#F59E0B", display: "inline-flex", alignItems: "center", gap: 3 }}>
          <AlertTriangle style={{ width: 10, height: 10 }} />
          {warning}
        </span>
      )}
    </div>
  );
}

const inputSt: React.CSSProperties = {
  width: "100%", padding: "7px 10px", borderRadius: 6,
  border: "1px solid var(--border)",
  backgroundColor: "var(--muted)",
  fontSize: 12, color: "var(--foreground)",
  outline: "none", boxSizing: "border-box",
};

const readonlyTitle: React.CSSProperties = {
  fontSize: 13, fontWeight: 600, color: "var(--foreground)",
  overflowWrap: "anywhere", wordBreak: "break-word",
};

const readonlyBody: React.CSSProperties = {
  fontSize: 12, color: "var(--foreground)",
  lineHeight: 1.6,
  maxHeight: 260, overflowY: "auto",
  padding: "4px 0",
  overflowWrap: "anywhere", wordBreak: "break-word",
};

const em: React.CSSProperties = { color: "var(--muted-foreground)" };

const tagChip: React.CSSProperties = {
  padding: "1px 7px", borderRadius: 999, fontSize: 10,
  backgroundColor: "rgba(41,98,255,0.08)", color: "#2962FF",
  border: "1px solid rgba(41,98,255,0.2)",
};